/**
 * Type definitions for the qduels-api `/v1/*` namespace.
 *
 * QDuels is the QLASH 1v1 duel-challenge microservice (Clash Royale today):
 * the challenger picks a friend, qduels creates a `pending` challenge, the
 * defender accepts/declines in-app, and on accept BOTH players are told to open
 * Clash Royale and play a friendly battle manually — the Supercell API is
 * read-only, so qduels does NOT (and cannot) create an in-game invite; instead
 * a polling worker detects the played match and tallies the result.
 *
 * Hand-copied from the qduels-api contract (no codegen) — keep in sync when
 * the server contract changes. Endpoint base: `https://qduels.qlash.gg`.
 */

/** Best-of length for a duel. Defaults to `bo3` in the UI. */
export type DuelFormat = 'bo1' | 'bo3' | 'bo5';

/**
 * Game modes exposed in the duel picker. qduels-api accepts `gameMode` as a
 * free string server-side (any Supercell CrGameMode); the Mini App only
 * surfaces this curated set. NB: `duel` MUST stay lowercase — Supercell expects
 * it that way (all the others are upper-case). `duel` is itself a best-of-3
 * within one match, so combined with a bo3/bo5 series it's heavy — kept on
 * purpose, just be aware.
 */
export type DuelGameMode =
  | 'REGULAR'
  | 'duel'
  | 'DOUBLE_ELIXIR'
  | 'TRIPLE_ELIXIR'
  | 'SUDDEN_DEATH'
  | 'RAMP_UP'
  | 'TOUCHDOWN'
  | 'HEIST';

/** Game modes shown in the challenge picker, in display order (REGULAR default). */
export const DUEL_GAME_MODES: readonly DuelGameMode[] = [
  'REGULAR',
  'duel',
  'DOUBLE_ELIXIR',
  'TRIPLE_ELIXIR',
  'SUDDEN_DEATH',
  'RAMP_UP',
  'TOUCHDOWN',
  'HEIST',
];

/** Best-of options shown in the challenge picker (bo3 default). */
export const DUEL_FORMATS: readonly DuelFormat[] = ['bo1', 'bo3', 'bo5'];

/** i18n key for a best-of label — e.g. 'bo3' → 'social.duel_format_bo3'. */
export function duelFormatLabelKey(format: string): string {
  return `social.duel_format_${format}`;
}

/** i18n key for a game-mode label — e.g. 'TOUCHDOWN' → 'social.duel_mode_TOUCHDOWN'. */
export function duelGameModeLabelKey(mode: string): string {
  return `social.duel_mode_${mode}`;
}

/** Surface that originated the challenge (telemetry on the qduels side). */
export type DuelCreatedVia = 'telegram';

/**
 * Lifecycle status of a challenge (mirrors qduels-api `Challenge.status`).
 * `pending` → defender accepts → `in_progress` (a hosted Supercell match is
 * created and both players get the in-game invite) → `completed`. Other
 * terminal states: `declined`, `cancelled`, `expired`, `abandoned`.
 */
export type ChallengeStatus =
  | 'pending'
  | 'accepted'
  | 'in_progress'
  | 'declined'
  | 'expired'
  | 'cancelled'
  | 'completed'
  | 'abandoned';

/**
 * Request body for `POST /v1/challenges`.
 *
 * `defenderLogtoId` is the friend's canonical QLASH user id (Logto `sub`) —
 * the same value QSocials stores as `friendUserId`. The challenger is taken
 * from the Bearer JWT `sub` server-side, so it is not sent here.
 */
export interface CreateChallengeRequest {
  defenderLogtoId: string;
  format: DuelFormat;
  gameMode: DuelGameMode;
  createdVia: DuelCreatedVia;
}

/** Participant projection inside a challenge object (qduels `challenger`/`defender`). */
export interface ChallengeParticipant {
  logtoUserId: string;
  playerTag: string;
  displayName?: string | null;
}

/**
 * A challenge object as returned by qduels-api (`POST /v1/challenges` 201,
 * `GET /v1/challenges` list items, and the accept/decline/cancel responses).
 * The server returns the full document unwrapped (no `{ data }` envelope).
 */
export interface ChallengeDTO {
  challengeId: string;
  status: ChallengeStatus;
  challenger: ChallengeParticipant;
  defender: ChallengeParticipant;
  format: DuelFormat;
  gameMode?: string;
  winsChallenger?: number;
  winsDefender?: number;
  currentMatchTag?: string | null;
  /**
   * CR game tags of the matches played in this challenge (bo3/bo5), in order.
   * Present in the qduels-api response; the last entry is the decisive/most
   * recent match — what the Sfide-tab "completed" tap opens in DuelResult.
   */
  matchTags?: string[];
  winnerLogtoUserId?: string | null;
  resultReason?: string | null;
  /** ISO timestamp after which a still-`pending` challenge auto-expires. */
  deadline?: string;
  createdVia?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Query for `GET /v1/challenges` (all optional). */
export interface ListChallengesQuery {
  role?: 'challenger' | 'defender';
  status?: ChallengeStatus;
  limit?: number;
}

/**
 * Error envelope for qduels 4xx responses: `{ "error": "<code>" }`.
 * Known codes the Mini App handles explicitly are enumerated in
 * `QduelsErrorCode`; any other string falls through to a generic message.
 */
export interface QduelsErrorBody {
  error: string;
}

/** Error codes qduels-api returns that the friends-list UI maps to copy. */
export type QduelsErrorCode =
  | 'defender_not_linked'
  | 'challenger_not_linked'
  | 'cannot_challenge_self'
  | 'missing_bearer_token'
  | 'invalid_token'
  // verify-token / link-status codes (POST /v1/players/verify-token, GET /v1/players/me)
  | 'player_tag_already_linked'
  | 'invalid_player_tag'
  | 'no_linked_player'
  // matchmaking-queue codes (POST /v1/queue/enter)
  | 'player_not_linked' // 400 — caller has no linked CR account
  | 'not_eligible' // 403 — CR account below the floor (≥32 cards, level ≥10)
  | 'already_in_match'; // 409 — caller already has a live match

// ─── Account linking (Clash Royale) ──────────────────────────────────────

/**
 * Request body for `POST /v1/players/verify-token`.
 *
 * Supercell verifies account ownership via a short-lived token the user
 * generates inside Clash Royale (Settings → API token for third-party
 * verification). The user pastes that token + their player tag; qduels checks
 * both against Supercell and, on success, links the caller's logtoUserId to
 * that CR tag.
 *
 * `playerTag` must include the leading `#` — the UI accepts it with or without
 * and normalizes before sending.
 */
export interface VerifyTokenRequest {
  playerTag: string;
  token: string;
}

/**
 * Response body for `POST /v1/players/verify-token`.
 * `verified: true` means the caller is now linked to `playerTag`. A wrong or
 * expired in-game token comes back as HTTP 422 with `{ verified: false }`.
 */
export interface VerifyTokenResponse {
  verified: boolean;
  playerTag: string;
}

/**
 * Response body for `GET /v1/players/me` (HTTP 200) — the caller's current CR
 * link. A 404 with `{ error: "no_linked_player" }` means the user has not
 * linked any Clash Royale account yet (treated as "not linked", not an error).
 */
export interface PlayerLinkDTO {
  logtoUserId: string;
  playerTag: string;
  verified: boolean;
  verifiedAt?: string | null;
}

/**
 * Minimal cached Clash Royale profile projection from
 * `GET /v1/players/:playerTag/profile`. Used only to show the linked name +
 * a couple of headline stats; optional, the link form works without it.
 */
export interface CrProfileDTO {
  name: string;
  expLevel?: number;
  trophies?: number;
  [key: string]: unknown;
}

/**
 * Response body for `GET /v1/players/by-cr-tag/:tag` (HTTP 200) — the identity
 * bridge for the CR side. A battle opponent / CR profile is known only by CR
 * tag (no email), so this maps a CR tag → Logto `sub` for players who linked
 * their account in qduels. 404 `{ error: "player_not_found" }` means the tag
 * isn't linked (treated as "not resolved", NOT an error); 400
 * `{ error: "invalid_player_tag" }` for a malformed tag.
 */
export interface PlayerByCrTagDTO {
  logtoUserId: string;
}

// ─── Open matchmaking queue (Clash Royale) ───────────────────────────────

/**
 * The match currently assigned to the caller while they're in the queue, as
 * projected inside `GET /v1/queue/status`. It carries ONLY the match's CR game
 * tag, lifecycle state, and game mode — NOT the opponent's tag (that would need
 * a separate `GET /v1/matches/:gameTag`). `currentMatch != null` is the "paired"
 * signal the QueueSheet flips to the "found" state on.
 */
export interface QueueCurrentMatch {
  gameTag: string;
  state: string;
  gameMode: string;
}

/**
 * Response body for `GET /v1/queue/status` (HTTP 200) — the caller's live
 * matchmaking state. Polled (~3s) only while the QueueSheet is open.
 * - `inQueue` / `isSearching`: whether the caller has an active queue entry.
 * - `position`: 1-based slot in the queue (0 when not queued).
 * - `searching`: total players currently searching the same pool — surfaced as
 *   "{n} in coda" and used to surface the friend CTA early when `<= 1`.
 * - `gameMode`: the pool the caller is searching (fixed `REGULAR` in v1).
 * - `currentMatch`: the assigned match once paired, else `null`.
 * - `lastRun`: ISO timestamp of the last pairing-worker run (diagnostic).
 */
export interface QueueStatus {
  inQueue: boolean;
  isSearching: boolean;
  position: number;
  searching: number;
  gameMode: string;
  currentMatch: QueueCurrentMatch | null;
  lastRun: string | null;
}

/**
 * Request body for `POST /v1/queue/enter`. v1 always sends the single fixed
 * pool (`REGULAR`) and a single-game series (`bo1`) — no mode/format picker.
 */
export interface QueueEnterRequest {
  gameMode: DuelGameMode;
  format?: DuelFormat;
}

/**
 * Response body for `POST /v1/queue/enter` (HTTP 201) — the created (or, on a
 * re-enter, updated/idempotent) queue entry. The Mini App only needs to know
 * the request succeeded and then drives off `GET /v1/queue/status`, so we keep
 * the projection minimal and tolerant.
 */
export interface QueueEntry {
  inQueue: boolean;
  gameMode: string;
  position?: number;
  format?: string;
}

// ─── Match detail + post-match stats ─────────────────────────────────────

/**
 * A completed (or in-flight) match document from `GET /v1/matches/:gameTag`.
 * Mirrors qduels-api's `Match` model. Returned unwrapped (no `{ data }`).
 *
 * Identity is by CR tag: `playerTagA`/`playerTagB` are the two players,
 * `scoreA`/`scoreB` their crowns (REGULAR) or rounds (`duel` mode), and
 * `winner` is the winning player's CR tag (or `null` for a draw / no result).
 * The caller maps "me" by comparing their own CR tag (from `getMyLink`) to
 * `playerTagA`/`playerTagB`.
 */
export interface MatchDTO {
  gameTag: string;
  gameMode: string;
  state: string;
  playerTagA: string;
  playerTagB: string;
  /** Winning player's CR tag, or `null` for a draw / unresolved. */
  winner: string | null;
  /** Crowns (REGULAR) or rounds won (`duel`) for playerTagA / playerTagB. */
  scoreA: number;
  scoreB: number;
  pointsA: number;
  pointsB: number;
  /** `crowns` | `rounds` | `forfeit` | `timeout` | `random`, or null. */
  resultReason: string | null;
  replayTag?: string | null;
  challengeId?: string | null;
  queuePairId?: string | null;
  completedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Per-player battle stats for one side of a match. Mirrors qduels-api's
 * `PlayerBattleStats` (domain/stats.ts). Each entry carries its CR `tag`, so
 * the caller attributes the "team"/"opponent" arrays to you vs the opponent by
 * tag rather than by array position.
 */
export interface PlayerBattleStats {
  tag: string;
  name: string;
  crowns: number;
  princessTowersHitPoints?: number[];
  kingTowerHitPoints?: number;
  elixirLeaked?: number;
  startingTrophies: number | null;
  globalRank: number | null;
  averageElixirCost: number;
  totalElixirCost: number;
  averageCardLevel: number;
  rarityDistribution: Record<string, number>;
  elixirDistribution: Record<string, number>;
  cycleCards: number;
  winConditions: number;
  spells: number;
  airTargetingCards: number;
  evolutionCapableCards: number;
}

/**
 * One player's deck as a Clash Royale "copy deck" deep link. Mirrors
 * qduels-api's `DeckDeeplink` (domain/deckLinks.ts). `cardIds` is a
 * semicolon-joined list of numeric card ids (`"26000000;..."`), `web` the
 * ready-to-open `link.clashroyale.com` URL, `native` the raw
 * `clashroyale://copyDeck…` scheme. Each entry carries its CR `tag`.
 */
export interface DeckDeeplink {
  tag: string;
  name: string;
  native: string;
  web: string;
  cardIds: string;
}

/**
 * Post-match stats bundle from `GET /v1/matches/:gameTag/stats`. Mirrors
 * qduels-api's `MatchStats` model. `stats`/`copyDecks` each split the two
 * players into `team` / `opponent` arrays — attribute by the `tag` each entry
 * carries, NOT by which array it's in.
 */
export interface MatchStatsDTO {
  gameTag: string;
  gameMode: string;
  replayTag: string | null;
  stats: {
    team: PlayerBattleStats[];
    opponent: PlayerBattleStats[];
  };
  copyDecks: {
    team: DeckDeeplink[];
    opponent: DeckDeeplink[];
  };
  viewCount: number;
  shareCount: number;
}
